import React, { useContext, useEffect, useRef } from "react";
import * as THREE from "three";

import { SharedMaterialsStateContext } from "../store/sharedMaterialsState/SharedMaterialsStateProvider";

import Material from "../model/material/Material";
import MaterialTypeEnum from "../enums/MaterialTypeEnum";
import ColorMaterial from "../model/material/ColorMaterial";
import { SharedMaterialsStateActionEnum } from "../store/sharedMaterialsState/interfaces";

import MaterialPair from "../model/material/MaterialPair";
import { useProductModelStore } from "../store/productModelStore";
import { useViewerStateStore } from "../store/viewerStateStore";
import { IMaterialSet } from "../services/MaterialManager";
import TextureMaterial from "../model/material/TextureMaterial";

const loader = new THREE.TextureLoader();
const azurePath = "https://gb3dassets.z6.web.core.windows.net/assetbundles/Components/";

// Releases GPU resources held by a set of material pairs.
export const disposeMaterialPairs = (pairs: Record<number, MaterialPair>) => {
    Object.values(pairs).forEach(pair => {
        const material = pair.threeJsMaterial;
        if (!material) return;
        if (material instanceof THREE.MeshStandardMaterial) {
            material.map?.dispose();
            material.normalMap?.dispose();
        } else if (material instanceof THREE.MeshBasicMaterial) {
            material.map?.dispose();
        }
        material.dispose();
    });
};

//https://gb3dassets.z6.web.core.windows.net/assetbundles/Components/CENT_Dyyni_Sisustuspaneeli/CENT_Dyyni_Sisustuspaneeli.png

const createMaterialPairs = (materials: Material[], isDebugMode: boolean  ) : Record<number, MaterialPair> => {
    const threeJsMaterialTypes = [MaterialTypeEnum.ColorMaterial,MaterialTypeEnum.TextureMaterial];
                                 
    const materialPairs : Record<number, MaterialPair> = {};
        materials.forEach( m => {
            materialPairs[m.id] = new MaterialPair(m, threeJsMaterialTypes.includes(m.materialType) 
                                                                ? createThreeJsMaterial(m) 
                                                                : undefined);
        });

    return materialPairs;
}

const createThreeJsMaterial = (input: Material) : THREE.Material => {

    

    switch (input.materialType)
    {
        case MaterialTypeEnum.ColorMaterial:
            const cm = input as ColorMaterial;
            return new THREE.MeshBasicMaterial({color: cm.color, transparent: cm.opacity < 1.0, opacity: cm.opacity });  // TODO: had to disable support for opacity since it was causing issues where text would not be visible, even on top of this material
            //return new THREE.MeshBasicMaterial({color: cm.color});        

        case MaterialTypeEnum.TextureMaterial:
            const tm = input as TextureMaterial;
            
            const mainTexture = loader.load(azurePath + tm.mainTextureUrl);

            mainTexture.wrapS = THREE.RepeatWrapping;
            mainTexture.wrapT = THREE.RepeatWrapping;
            /* mainTexture.repeat.set(2, 2);  */

            let normalTexture : THREE.Texture | undefined;
            if (tm.normalMapUrl)
            {
                normalTexture = loader.load(azurePath + tm.normalMapUrl);
                normalTexture.wrapS = THREE.RepeatWrapping;
                normalTexture.wrapT = THREE.RepeatWrapping;
            }

            let commonParams = {
                map: mainTexture,           // base color / albedo
                // You can add more maps later: normalMap, roughnessMap, etc.
                metalness: 0.1,
                roughness: 0.7,
            };

            if (normalTexture) {
                return new THREE.MeshStandardMaterial({ ...commonParams, normalMap: normalTexture });
            }


            return new THREE.MeshStandardMaterial(commonParams);
    }

    throw "Invalid material";
    
}

const SharedMaterialsCreator = () =>
{
    const { materials } = useProductModelStore();
    const isDebugMode = useViewerStateStore(state => state.isDebugMode);
    const { dispatch: materialsDispatch } = useContext(SharedMaterialsStateContext);
    const previousMaterialPairsRef = useRef<Record<number, MaterialPair> | null>(null);

    // When materials change, create three.js materials from them
     useEffect( () => {

        const materialPairs = createMaterialPairs(materials, isDebugMode);
        previousMaterialPairsRef.current = materialPairs;
        materialsDispatch({type: SharedMaterialsStateActionEnum.SET_MATERIALS, data: materialPairs });

        return () => {
            // Dispose GPU resources when the effect re-runs (new materials) or component unmounts.
            if (previousMaterialPairsRef.current) {
                disposeMaterialPairs(previousMaterialPairsRef.current);
                previousMaterialPairsRef.current = null;
            }
        };

      }, [materials, isDebugMode]);
    

    return <></>
};

export default SharedMaterialsCreator;
export { createMaterialPairs };